import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

const pending = new Set();
document.addEventListener(StandardEvents.cartLinesUpdate, event => {
  if (!event.promise) return;
  pending.add(event.promise);
  event.promise.then(() => pending.delete(event.promise), () => pending.delete(event.promise));
});
const colors = ["Nude","Black","Taupe Brown","Cherry Red","Blush Pink","Olive Green","Blue Gray","Pure White","Turquoise"];
const sizes = ['S','M','L','XL','2XL','3XL','4XL','5XL','6XL'];
const giftSizes = ['S','M','L','XL','2XL','3XL','4XL'];

// UI countdown requested for the cart; this does not reserve inventory.
const reservationKey = 'qlayon-cart-e-countdown-v1';
let reservationDeadline = 0;
try { const saved = Number(sessionStorage.getItem(reservationKey)); if (Number.isFinite(saved) && saved > 0) reservationDeadline = saved; } catch {}
function reservationTime(deadline, now) {
  const seconds = Math.max(0, Math.ceil((deadline - now) / 1000));
  return {expired: seconds === 0, text: String(Math.floor(seconds / 60)).padStart(2,'0') + ':' + String(seconds % 60).padStart(2,'0')};
}


class QlayonCartE extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.reservationInterval = setInterval(() => this.tickReservation(), 1000);
    this.closest('theme-drawer')?.addEventListener('theme-drawer:open', () => this.tickReservation(), {signal});
    document.addEventListener('visibilitychange', () => this.tickReservation(), {signal});
    queueMicrotask(() => this.tickReservation());
    this.addEventListener('change', event => {
      if (!event.target.matches('[data-qce-quantity]')) return;
      const input = event.target;
      if (!input.reportValidity()) return;
      const quantity = Number(input.value);
      const line = input.closest('[data-line-key]');
      if (quantity === Number(line.dataset.quantity)) return;
      if (!Number.isInteger(quantity) || quantity < 1) return;
      this.change(line, { quantity });
    }, {signal});
    this.addEventListener('click', event => {
      const button = event.target.closest('button');
      if (!button) return;
      const line = button.closest('[data-line-key]');
      if (button.hasAttribute('data-qce-step')) {
        if (this.busy || document.documentElement.hasAttribute('data-layr-cart-busy')) return;
        const input = line.querySelector('[data-qce-quantity]');
        const previous = input.value;
        if (Number(button.dataset.qceStep) > 0) input.stepUp();
        else input.stepDown();
        if (input.value !== previous) input.dispatchEvent(new Event('change', {bubbles: true}));
        return;
      }
      if (button.hasAttribute('data-qce-remove')) this.change(line, {quantity: 0});
      if (button.hasAttribute('data-qce-cancel')) {
        const editor = line.querySelector('details');
        editor.querySelectorAll('select').forEach(select => {
          select.value = [...select.options].find(option => option.defaultSelected)?.value || '';
        });
        editor.open = false;
        editor.querySelector('summary').focus();
      }
      if (button.hasAttribute('data-qce-save')) {
        const changes = {};let giftProperties;
        for (const field of line.querySelectorAll('[data-component]')) {
          const color = field.querySelector('[data-color]');
          const size = field.querySelector('[data-size]');
          if (!color.reportValidity() || !size.reportValidity()) return;
          const gift = field.dataset.component === 'Free gift';
          if (!(gift ? ['Nude','Black'] : colors).includes(color.value) || !(gift ? giftSizes : sizes).includes(size.value)) return;
          if(gift&&line.dataset.giftKey){giftProperties={Size:size.value,'Brief 1':color.value};continue;}
          changes[field.dataset.component] = `${gift ? '1 high-waisted brief · ' : ''}${color.value} / ${size.value}`;
        }
        this.change(line, {properties: changes, giftProperties});
      }
    }, {signal});
    this.addEventListener('submit', event => {
      if (document.documentElement.hasAttribute('data-layr-cart-busy') || pending.size) {
        event.preventDefault();
        this.message('Your cart is updating. Please try checkout in a moment.');
      }
    }, {signal});
  }
  disconnectedCallback() { this.controller?.abort(); this.controller = null; clearInterval(this.reservationInterval); }
  tickReservation() {
    const timer = this.querySelector('[data-qce-timer]');
    if (!timer) {
      if (this.querySelector('.qce-empty')) {
        reservationDeadline = 0;
        try { sessionStorage.removeItem(reservationKey); } catch {}
      }
      return;
    }
    const now = Date.now();
    if (!reservationDeadline || reservationDeadline <= now) {
      if (this.closest('theme-drawer') && !this.closest('theme-drawer').hasAttribute('open')) return;
      reservationDeadline = now + 10 * 60 * 1000;
      try { sessionStorage.setItem(reservationKey, String(reservationDeadline)); } catch {}
    }
    const remaining = reservationTime(reservationDeadline, now);
    timer.textContent = remaining.text;
    this.querySelector('[data-qce-reservation-label]').textContent = 'Your cart is reserved for';
  }
  message(text) {
    const node = document.querySelector('#cart-drawer [data-qce-global-error]') || this.querySelector('[data-qce-error]');
    if (node) { node.textContent = text; node.hidden = !text; }
  }
  async change(line, changes) {
    if (this.busy || document.documentElement.hasAttribute('data-layr-cart-busy')) return;
    this.busy = true;
    this.message('');
    const base = (this.dataset.rootUrl || '/').replace(/\/$/, '');
    const source = this.closest('cart-items-component');
    const sectionId = source.dataset.sectionId;
    const mode = source.hasAttribute('data-drawer') ? 'hydration' : 'full';
    const key = line.dataset.lineKey;
    const oldQuantity = Number(line.dataset.quantity);
    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    let locks = [], controls = [], ownsLock = false, requestSent = false, confirmed = false;
    const sections = [...new Set([sectionId, ...[...document.querySelectorAll('cart-items-component')].map(c => c.dataset.sectionId).filter(Boolean)])].slice(0,5);
    const getCart = async () => {
      const response = await fetch(`${base}/cart.js`, {credentials:'same-origin', headers:{Accept:'application/json'}, cache:'no-store'});
      if (!response.ok) throw new Error('Unable to refresh your cart. Please try again.');
      return response.json();
    };
    const request = async (path, payload) => {
      const response=await fetch(`${base}/${path}`,{method:payload?'POST':'GET',credentials:'same-origin',cache:'no-store',headers:{Accept:'application/json',...(payload?{'Content-Type':'application/json'}:{})},...(payload?{body:JSON.stringify(payload)}:{})});
      const result=await response.json();if(!response.ok||result.errors)throw new Error(result.description||result.message||'Unable to update your bag.');return result;
    };
    try {
      await Promise.allSettled([...pending]);
      if (document.documentElement.hasAttribute('data-layr-cart-busy')) throw new Error('Your cart is updating. Please try again in a moment.');
      document.documentElement.setAttribute('data-layr-cart-busy',''); ownsLock = true;
      locks = [...document.querySelectorAll('cart-items-component')].map(el => ({el, inert:el.inert}));
      locks.forEach(({el}) => {el.inert = true; el.setAttribute('aria-busy','true');});
      controls = [...this.querySelectorAll('button,input,select')].map(el => ({el, disabled:el.disabled}));
      controls.forEach(({el}) => el.disabled = true);
      source.dispatchEvent(new CartLinesUpdateEvent({action:changes.quantity===0?'clear':'change',context:'cart',lines:[],promise:deferred.promise}));
      const before = await getCart();
      const current = before.items.find(item => item.key === key);
      if (!current || current.quantity !== oldQuantity) throw new Error('Your cart changed. Review the updated selection and try again.');
      const body = {id:key, quantity:changes.quantity ?? current.quantity, sections:sections.join(','), sections_url:window.location.pathname};
      if (changes.properties) body.properties = {...current.properties, ...changes.properties};
      const group=current.properties?._qlayon_bundle_id;
      const pairedGift=group&&current.properties?._qlayon_role==='tank-bundle' ? before.items.find(i=>i.product_id===9626299629736&&i.properties?._qlayon_bundle_id===group&&i.properties?._qlayon_role==='brief-gift') : null;
      let endpoint='change';
      if(pairedGift&&changes.quantity!==undefined){
        delete body.id;delete body.quantity;body.updates={[key]:changes.quantity,[pairedGift.key]:changes.quantity};endpoint='update';
      }
      if(changes.giftProperties){
        if(!pairedGift)throw new Error('Your gift changed. Please review your cart.');
        requestSent=true;
        await QlayonSkuRouting.replaceLine({request,current:pairedGift,properties:{...pairedGift.properties,...changes.giftProperties}});
      }
      requestSent = true;
      let cart;
      if(changes.properties){
        cart=await QlayonSkuRouting.replaceLine({request,current,properties:body.properties,sections:body.sections,sectionsUrl:body.sections_url});
      }else{
        cart=await request(`cart/${endpoint}.js`,body);
      }
      confirmed = true;
      const html = cart.sections?.[sectionId];
      if (html) await morphSection(sectionId, html, {mode});
      else await sectionRenderer.renderSection(sectionId,{cache:false,mode});
      if (changes.properties) document.querySelectorAll('#cart-drawer .qce-editor[open]').forEach(editor => editor.open = false);
      deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart),detail:{items:cart.items,itemCount:cart.item_count,sections:cart.sections,source:'qlayon-cart-e',didError:false}});
      const target = document.querySelector('#cart-drawer [data-qce-checkout]') || document.querySelector('#cart-drawer .qce-close');
      target?.focus({preventScroll:true});
    } catch(error) {
      // Reconcile once; never repeat a mutation after an uncertain response.
      try {
        const cart = await getCart();
        await sectionRenderer.renderSection(sectionId,{cache:false,mode});
        deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart),detail:{items:cart.items,itemCount:cart.item_count,source:'qlayon-cart-e',didError:true}});
      } catch { deferred.reject(error); }
      this.message(confirmed ? 'Your cart was updated. Please review it before checkout.' : requestSent ? `${error.message} Please review your cart before trying again.` : error.message);
    } finally {
      if (ownsLock) {
        document.documentElement.removeAttribute('data-layr-cart-busy');
        locks.forEach(({el,inert}) => {el.inert=inert;el.removeAttribute('aria-busy');});
        controls.forEach(({el,disabled}) => el.disabled=disabled);
        document.querySelectorAll('[data-qce-checkout]').forEach(button=>{button.disabled=button.dataset.qceDuplicate==='true';button.setAttribute('aria-disabled',String(button.disabled));});
        document.querySelectorAll('#cart-drawer [data-qce-step]').forEach(button => {
          const input = button.closest('.qce-quantity-control').querySelector('[data-qce-quantity]');
          const quantity = Number(input.value);
          const step = Number(input.step) || 1;
          button.disabled = Number(button.dataset.qceStep) < 0
            ? quantity - step < Number(input.min || 1)
            : input.max !== '' && quantity + step > Number(input.max);
        });
      }
      this.busy = false;
    }
  }
}
if (!customElements.get('qlayon-cart-e')) customElements.define('qlayon-cart-e',QlayonCartE);
